<script setup lang="ts">
// Components
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import PreparingOffer from "@/apps/merchant3/components/offers/v2/preparing/PreparingOffer.vue";
import UserNotApproved from "@/apps/merchant3/components/offers/v2/preparing/UserNotApproved.vue";
import NavigationFooter from "@/apps/merchant3/components/offers/v2/preparing/NavigationFooter.vue";

// Hooks
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import useIntervalCustomerState from "@/apps/merchant3/hooks/useIntervalCustomerState";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";

// Services
import { AxiosError, AxiosResponse } from "axios";
import { ref, onMounted, computed } from "vue";
import { getUserIsApproved } from "@/apps/merchant3/services/offers-v2-set.service";
import { onBeforeUnmount } from "vue";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import { clearStoreAndNavigateHome } from "@/apps/merchant3/helpers/redirect.helper";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";

enum UserIsApprovedResponse {
  ELIGIBLE = 6,
  WAITING_FOR_CX = 7,
  // NOT_ELIGIBLE = 10120,
}

enum UserNotApprovedResponse {
  NOT_ELIGIBLE = 10120,
}

const store = useStore();

const { t } = useI18n();
// Props
defineProps<{
  offerStep?: string;
}>();

// Emits
const emit = defineEmits<{
  (e: "close"): void;
}>();

// State
const currentState = ref<
  "preparing" | "notApproved" | "approved" | "waiting4Cx"
>("preparing");
const isLoading = ref<boolean>(true);

const errorMessage = ref<string>("");

const cartTag = computed(() => {
  return store.state.carts?.cartTag || "";
});
// Add these new refs at the top with your other state declarations
const pollingInterval = ref<number | null>(null);
const MAX_POLLING_ATTEMPTS = 360; // 30 minutes (300 * 5 seconds)
const pollingAttempts = ref(0);

// Router instance
const router = useRouter();

// Lifecycle: Mounted
onMounted(() => {
  useIntervalCustomerState();
  useMerchantPersistState(() => true);
  useCartPersistState(() => {
    checkEligibility();
    return true;
  });
});

// Functions
const checkEligibility = async (): Promise<void> => {
  isLoading.value = true;

  if (!cartTag.value) {
    currentState.value = "notApproved";
    isLoading.value = false;
    return;
  }

  // Clear any existing interval
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }

  // Start polling
  await pollEligibility();
};

const handleError = (error: AxiosError): void => {
  isLoading.value = false;
  if (error?.response?.data) {
    if (
      error.response.data?.errorCode &&
      error.response.data.errorCode !== UserNotApprovedResponse.NOT_ELIGIBLE
    ) {
      errorMessage.value = t("ERROR_GENERIC", {
        error: getAPIErrorMessage(error.response.data, t),
      });
    }
  } else {
    errorMessage.value = t("ERROR_GENERIC", { error: "Unknown error" });
  }
  currentState.value = "notApproved";

  // Stop polling on error
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

const pollEligibility = async (): Promise<void> => {
  try {
    const response: AxiosResponse<{ code: number; message: string }> =
      await getUserIsApproved(cartTag.value);

    if (response.data.code === UserIsApprovedResponse.ELIGIBLE) {
      // User is approved, stop polling and navigate
      currentState.value = "approved";
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
      await router.push({ name: RouteName.M3SelectingOffersV2, params: { countryCode: getCountryCode() } });
    } else if (response.data.code === UserIsApprovedResponse.WAITING_FOR_CX) {
      // Still processing, continue polling
      pollingAttempts.value++;
      currentState.value = "waiting4Cx";
      // If first attempt, set up the interval
      if (!pollingInterval.value) {
        pollingInterval.value = setInterval(
          pollEligibility,
          5000
        ) as unknown as number;
      }

      // Stop polling after max attempts
      if (pollingAttempts.value >= MAX_POLLING_ATTEMPTS) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
        currentState.value = "notApproved";
      }
    } else {
      // Other response code, treat as not approved
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
      currentState.value = "notApproved";
    }
  } catch (error: unknown) {
    handleError(error);
  } finally {
    isLoading.value = false;
  }
};

// Clean up on the component unmount
onBeforeUnmount(() => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
});

const handleClose = async (): Promise<void> => {
  emit("close");
  await clearStoreAndNavigateHome();
};
</script>

<template>
  <div class="offers-preparation d-flex flex-column vh-100">
    <!-- Main Content -->
    <div
      class="flex-grow-1 d-flex flex-column justify-content-center align-items-center p-3"
    >
      <Alert
        class="m-1 mb-2"
        v-if="errorMessage"
        :message="errorMessage"
        alertType="alert-danger"
        :closable="true"
        @onClose="errorMessage = ''"
      />
      <!-- Preparing Offer -->
      <PreparingOffer
        v-if="currentState === 'waiting4Cx' || currentState === 'preparing'"
      />

      <!-- Not Approved -->
      <UserNotApproved
        v-else-if="currentState === 'notApproved'"
        @close="handleClose"
      />
    </div>

    <!-- Footer -->
    <NavigationFooter />
  </div>
</template>

<style scoped>
.offers-preparation {
  background-color: #ffffff;
}
</style>
